import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./BrandStatement.css";

gsap.registerPlugin(ScrollTrigger);

function BrandStatement() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const glow = glowRef.current;

    if (!section || !title || !glow) return;

    let moveGlowX;
    let moveGlowY;
    let moveTitleX;
    let moveTitleY;

    const ctx = gsap.context(() => {
      gsap.set(glow, {
        xPercent: -50,
        yPercent: -50,
        opacity: 0,
      });

      moveGlowX = gsap.quickTo(glow, "x", {
        duration: 0.18,
        ease: "power2.out",
      });

      moveGlowY = gsap.quickTo(glow, "y", {
        duration: 0.18,
        ease: "power2.out",
      });

      moveTitleX = gsap.quickTo(title, "x", {
        duration: 0.4,
        ease: "power2.out",
      });

      moveTitleY = gsap.quickTo(title, "y", {
        duration: 0.4,
        ease: "power2.out",
      });

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 78%",
          once: true,
        },
      });

      timeline
        .from(".brand-statement-line", {
          y: 65,
          opacity: 0,
          duration: 0.9,
          stagger: 0.12,
          ease: "power4.out",
        })
        .from(
          ".brand-statement-transition",
          {
            y: 40,
            opacity: 0,
            duration: 0.7,
            ease: "power3.out",
          },
          "-=0.45"
        )
        .from(
          ".brand-statement-accent",
          {
            y: 50,
            opacity: 0,
            duration: 0.85,
            ease: "power4.out",
          },
          "-=0.4"
        );
    }, section);

    const handleMouseEnter = (event) => {
      const bounds = section.getBoundingClientRect();

      const mouseX = event.clientX - bounds.left;
      const mouseY = event.clientY - bounds.top;

      gsap.set(glow, {
        x: mouseX,
        y: mouseY,
      });

      gsap.to(glow, {
        opacity: 1,
        duration: 0.2,
        overwrite: "auto",
      });
    };

    const handleMouseMove = (event) => {
      const bounds = section.getBoundingClientRect();

      const mouseX = event.clientX - bounds.left;
      const mouseY = event.clientY - bounds.top;

      const normalizedX = mouseX / bounds.width - 0.5;
      const normalizedY = mouseY / bounds.height - 0.5;

      moveGlowX(mouseX);
      moveGlowY(mouseY);

      moveTitleX(normalizedX * 2);
      moveTitleY(normalizedY * 1);
    };

    const handleMouseLeave = () => {
      gsap.to(glow, {
        opacity: 0,
        duration: 0.25,
        overwrite: "auto",
      });

      moveTitleX(0);
      moveTitleY(0);
    };

    section.addEventListener("mouseenter", handleMouseEnter);
    section.addEventListener("mousemove", handleMouseMove);
    section.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      section.removeEventListener("mouseenter", handleMouseEnter);
      section.removeEventListener("mousemove", handleMouseMove);
      section.removeEventListener("mouseleave", handleMouseLeave);

      ctx.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="brand-statement"
      id="about"
    >
      <div
        ref={glowRef}
        className="brand-statement-cursor-glow"
      />

      <div className="brand-statement-grid" />

      <div className="brand-statement-container">
        <div className="brand-statement-main">
          <h2
            ref={titleRef}
            className="brand-statement-title"
          >
            <span className="brand-statement-line">
              WE DON'T JUST
            </span>

            <span className="brand-statement-line">
              CREATE CONTENT
            </span>

            <span className="brand-statement-transition">
              WE CREATE
            </span>

            <span className="brand-statement-accent">
              BRAND EXPERIENCES
            </span>
          </h2>
        </div>

        <div className="brand-statement-background-glow" />
      </div>
    </section>
  );
}

export default BrandStatement;